import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated, useSpring } from 'react-spring';
import moment from 'moment';
import { useTheme } from 'emotion-theming';
import ResizeObserver from 'react-resize-observer';

import Text from '../../../../shared/components/Text/Text';
import SubText from '../../../../shared/components/SubText/SubText';
import Button from '../../../../shared/components/Button/Button';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import MessagesByDay from './MessagesByDay';
import MessagesScheduled from './MessagesScheduled';
import MessagesIsTyping from './MessagesIsTyping';
import useConversations from '../hooks/useConversations';
import {
  setIsScheduledOpen,
} from '../../ducks/conversationsActions';

const MessageBoxContainer = styled('div')`
  height: 100%;
  width: 100%;
  min-width: 425px;
  display: flex;
  padding-top: 25px;
  overflow-y: auto;
  background: #fbfbfb;
  flex-direction: column-reverse;
  flex-wrap: wrap;

  .schedule-button {
    align-self: center;
    margin: 20px 0px;
    width: 250px;
    z-index: 100;
  }
`;

const MessageListContainer = styled('div')`
  height: auto;
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  display: flex;
`;

const LoadMoreContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 20px 15px;
  justify-content: center;
  position: relative;
`;

const MessageBox = ({
  conversationMessages,
  scheduledMessages,
  setEditingMessageId,
  setIsEditing,
  messageFormRef,
}) => {
  const dispatch = useDispatch();
  const messagesBoxRef = useRef();
  const messagesRef = useRef();
  const scheduledMessagesRef = useRef([]);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const activeConversation = useSelector((state) => state.conversations.data.activeConversation);
  const messagesByDay = useSelector((state) => state.conversations.data.conversationsByDay);
  const isInitialMessagesFetch = useSelector(
    (state) => state.conversations.ui.isInitialMessagesFetch,
  );
  const isScheduledOpen = useSelector((state) => state.conversations.ui.isScheduledOpen);
  const isMoreMessagesLoading = useSelector(
    (state) => state.conversations.ui.isMoreMessagesLoading,
  );
  const isMoreMessagesEmpty = useSelector((state) => state.conversations.ui.isMoreMessagesEmpty);

  const theme = useTheme();

  const {
    handleFetchMoreMessages,
  } = useConversations();

  // React Spring animations for loading more, and auto scrolling.
  // Scrolling animation works by setting the scroll location from the tableScroll state
  const loadTransition = useSpring({
    position: 'absolute',
    opacity: isMoreMessagesLoading ? 1 : 0,
  });

  const endTransition = useSpring({
    position: 'absolute',
    opacity: isMoreMessagesEmpty ? 1 : 0,
  });

  const scheduledTransition = useSpring({
    height: isScheduledOpen ? (scheduledMessagesRef.current?.clientHeight ?? 0) : 0,
    opacity: isScheduledOpen ? 1 : 0,
  });

  const ScheduleContainer = styled('div')`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
  `;

  const scheduledMessagesMapper = (messages) => {
    if (messages.length !== 0) {
      return (
        <ScheduleContainer>
          <Button
            className='schedule-button'
            color={theme.colors.lightText[1]}
            onClick={() => {
              dispatch(setIsScheduledOpen());
            }}
            cta='Scheduled Messages'
            icon='clock'
          />
          <animated.div style={scheduledTransition}>
            <MessagesScheduled
              activeConversation={activeConversation}
              currentUser={currentUser}
              dayMessages={messages}
              isInitialMessagesFetch={isInitialMessagesFetch}
              messageFormRef={messageFormRef}
              scheduledMessagesRef={scheduledMessagesRef}
              setIsEditing={setIsEditing}
              setEditingMessageId={setEditingMessageId}
            />
          </animated.div>
        </ScheduleContainer>
      );
    }
    return null;
  };

  return (
    <MessageBoxContainer ref={messagesBoxRef}>
      <MessageListContainer ref={messagesRef}>
        {/*
          Instead of using a scroll event listener which can be expensive, we're using
          the resize observer api which determines changes to the dimensions and position
          information of an element. By placing it within the TableWrapper we are observing
          the leaderboard table and can use the rect.top data for scroll location.
        */}
        <ResizeObserver
          onPosition={(rect) => {
            // If rect.top from observer api is less than tableEnd from
            // the above variable, then load more data.
            if (rect.top >= 100
              && !isMoreMessagesLoading
              && messagesByDay.length > 0
              && conversationMessages.length > 49

            ) {
              handleFetchMoreMessages(currentUser.accountCode, activeConversation.id);
            }
          }}
        />
        {scheduledMessagesMapper(scheduledMessages)}
        <MessagesIsTyping />
        {
          messagesByDay.map((day) => (
            <>
              <MessagesByDay
                currentUser={currentUser}
                day={day[0]}
                dayMessages={day[1]}
                isInitialMessagesFetch={isInitialMessagesFetch}
              />
              {day[1].length !== 0 ? (
                <Text
                  fontSize={16}
                  style={{
                    alignSelf: 'center',
                    margin: '20px 0px',
                  }}
                  color={theme.colors.lightText[0]}
                >
                  {moment(day[0], 'MM/DD/YYYY')
                    .tz(currentUser.userSettings.timeZone)
                    .format('MMMM D')}
                </Text>
              ) : null}
            </>
          ))
        }
        <LoadMoreContainer>
          <animated.div style={loadTransition}>
            <Spinner
              saving
              darkTheme
            />
          </animated.div>
          <animated.div style={endTransition}>
            <SubText>
              Beginning of Conversation
            </SubText>
          </animated.div>
        </LoadMoreContainer>
      </MessageListContainer>
    </MessageBoxContainer>
  );
};

MessageBox.propTypes = {
  conversationMessages: PropTypes.instanceOf(Array).isRequired,
  scheduledMessages: PropTypes.instanceOf(Array).isRequired,
  setEditingMessageId: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  messageFormRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

export default MessageBox;
